/**
 * Sprint 23.8 — TabBar (shared sticky tab strip).
 *
 * Generalised from the Sprint 23.6 v2 AgentProfileTabs so the agent profile and
 * the workspace home share ONE tab bar instead of cloning the (subtle) active-
 * underline CSS. Flat text tabs, no icons, gap 14, font 12; the active tab gets
 * a 2px text-primary bottom border; counts are plain tertiary spans.
 *
 * The container surface (background / bottom-border colour / padding / bleed)
 * varies by host — the agent profile bleeds into its card's 20px padding; the
 * workspace home sits on the page canvas — so it arrives via `barStyle`. The
 * invariant tab CSS (the underline longhands) lives here, once.
 */

import { type CSSProperties } from "react";

export interface TabBarTab {
  key: string;
  label: string;
  /** Optional trailing count (rendered only when > 0). */
  count?: number;
}

export interface TabBarProps {
  tabs: TabBarTab[];
  /** Active tab key. */
  active: string;
  onSelect: (key: string) => void;
  /** Merged into the bar container — background, border colour, padding, bleed. */
  barStyle?: CSSProperties;
  ariaLabel?: string;
}

export function TabBar({ tabs, active, onSelect, barStyle, ariaLabel }: TabBarProps) {
  return (
    <nav style={{ ...bar, ...barStyle }} aria-label={ariaLabel}>
      {tabs.map((tab) => {
        const isActive = tab.key === active;
        return (
          <div
            key={tab.key}
            role="button"
            tabIndex={0}
            onClick={() => onSelect(tab.key)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onSelect(tab.key);
              }
            }}
            style={isActive ? tabActive : tabIdle}
            aria-current={isActive ? "page" : undefined}
          >
            {tab.label}
            {typeof tab.count === "number" && tab.count > 0 && (
              <span style={countSpan}> {tab.count}</span>
            )}
          </div>
        );
      })}
    </nav>
  );
}

// Invariant bar geometry. The surface (background / border / padding / margins)
// is supplied by `barStyle` per host — never hard-coded here.
const bar: CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 2,
  display: "flex",
  gap: 14,
  fontSize: 12,
  overflowX: "auto",
};

// A clean straight underline is the ONLY active indicator. CRITICAL: use the
// border-bottom *longhands* (width/style/color), NOT the `borderBottom`
// shorthand. Mixing the shorthand (in the base) with a `borderBottomColor`
// longhand (in active) means React, on active→idle, removes only the color
// longhand and the border falls back to `currentColor` (the gray text colour) —
// leaving a stuck gray underline on every tab that was ever active. Setting the
// color longhand explicitly in BOTH states resets it cleanly to transparent.
const tabBase: CSSProperties = {
  padding: "10px 0",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  whiteSpace: "nowrap",
  borderBottomWidth: 2,
  borderBottomStyle: "solid",
  borderBottomColor: "transparent",
  borderRadius: 0,
  outline: "none",
  userSelect: "none",
};

const tabIdle: CSSProperties = tabBase;

const tabActive: CSSProperties = {
  ...tabBase,
  color: "var(--color-text-primary)",
  borderBottomColor: "var(--color-text-primary)",
  fontWeight: 500,
};

const countSpan: CSSProperties = {
  color: "var(--color-text-tertiary)",
  fontWeight: 400,
};
